import { BasketApiService } from '@api_patient/basket_api.service';
import { GeneralApiService } from '@api_patient/general_api.service';
import { AnalyticCategories, AnalyticService } from '@application/analytic/analytic.service';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { UniversalConfig } from '@application/universal_config.service';
import { CellulaFormSubmitData } from '@cellula/react';
import { FileManagerService } from '@feature/file_manager/file_manager.service';
import type { CommitSuccessResult } from '@visiba/api-vcs-client-v2';
import { CommitResultStatus, QueryCommitStatus, ValidationResultType } from '@visiba/api-vcs-client-v2';
import { useResolve } from '@visiba/instantiation';
import { Logger } from '@visiba/logger';
import { delay } from '@visiba/std';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Basket } from '../../hooks/use-basket';
import { useBasket } from '../../hooks/use-basket';
import { CreateNewMessageAnalyticActions, useCreateNewMessage } from '../../hooks/use-create-new-message';

const COMMIT_STATUS_RETRY_COUNT = 30;
const COMMIT_STATUS_DELAY = 1000;

const logger = new Logger('useScreenConclusion');

export class NonRecoverableError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'NonRecoverableError';
	}
}

export function useScreenConclusion() {
	const { flowData, goToScreen, flowSettings } = useCreateNewMessage();
	const { basket, reasonForVisitId } = useBasket();
	const basketApiService = useResolve(BasketApiService);
	const fileManagerService = useResolve(FileManagerService);
	const analyticService = useResolve(AnalyticService);
	const generalApiService = useResolve(GeneralApiService);
	const universalConfig = useResolve(UniversalConfig);
	const [termsOfServiceUrl, setTermsOfServiceUrl] = useState<string | null>(null);
	// Mirrors the previous controller's private `basket` field. Captured on mount
	// so that `submit` operates on the same basket reference used for the
	// allowed-to-be-here check.
	const basketRef = useRef<Basket | null>(null);

	const allowedToBeHere = useCallback(
		(basket: Basket | null): boolean => {
			if (!basket) {
				logger.logInformation('No basket found.');
				return false;
			}

			if (flowData.flowState !== 'accessible') {
				logger.logInformation('FlowData not set.');
				return false;
			}

			const selectedReasonForVisit = flowData.value.reasonForVisits.find((rfv) => rfv.Id === reasonForVisitId) ?? null;

			if (flowData.value.reasonForVisits.length > 0 && !selectedReasonForVisit) {
				logger.logInformation('No reason for visit selected.');
				return false;
			}

			return true;
		},
		[reasonForVisitId, flowData],
	);

	useEffect(
		function whenMount() {
			analyticService.trackPageView({
				customTitle: 'Create new message: Conclusion',
			});
		},
		[analyticService],
	);

	useEffect(() => {
		if (!basket) {
			return;
		}

		basketRef.current = basket;

		if (!allowedToBeHere(basket)) {
			analyticService.trackEvent(
				AnalyticCategories.CreateNewMessage,
				CreateNewMessageAnalyticActions.NotAllowedToBeHere,
				'Conclusion',
			);

			logger.logInformation('Not allowed to be here. Redirect to start screen.');
			goToScreen('start');
			return;
		}

		let cancelled = false;

		(async () => {
			const policyDocumentsResult = await generalApiService.getPolicyDocuments(
				universalConfig.alias,
				universalConfig.selectedLanguageCode.peek(),
			);
			if (cancelled) return;
			if (policyDocumentsResult.ok) {
				setTermsOfServiceUrl(policyDocumentsResult.ok.data.TermsOfServiceUrl);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [allowedToBeHere, analyticService, basket, generalApiService, universalConfig, goToScreen]);

	const getCommitStatus = useCallback(
		async (commitJobId: number): AsyncResult<CommitSuccessResult> => {
			for (let count = 0; count < COMMIT_STATUS_RETRY_COUNT; count++) {
				const commitStatusResult = await basketApiService.getCommitStatus(commitJobId);

				if (commitStatusResult.err) return Err(commitStatusResult.err);

				if (commitStatusResult.ok.data.Status === QueryCommitStatus.CompletedOk) {
					return Ok(commitStatusResult.ok.data);
				}

				await delay(COMMIT_STATUS_DELAY);
			}

			return Err(new Error('Waited too long for commit status.'));
		},
		[basketApiService],
	);

	const waitForCommitStatus = useCallback(
		async (commitJobId: number): AsyncResult<{ messageConversationId: number | null }> => {
			const commitStatusResult = await getCommitStatus(commitJobId);
			if (commitStatusResult.err) return Err(new NonRecoverableError(commitStatusResult.err.message));

			const messageConversationId =
				commitStatusResult.ok.MessagingCases.length > 0 ? commitStatusResult.ok.MessagingCases[0].Id : null;

			return Ok({
				messageConversationId,
			});
		},
		[getCommitStatus],
	);

	const submit = useCallback(
		async (
			formData: CellulaFormSubmitData,
		): AsyncResult<{ commitJobId?: number; paymentBillId?: number; messageConversationId?: number | null }, Error | string> => {
			const basket = basketRef.current;
			if (!basket) {
				throw new Error('No basket found');
			}

			const reason =
				flowSettings.flowState === 'accessible'
					? (flowSettings.value.predefinedDescriptionOfMatter ?? formData.message ?? '')
					: (formData.message ?? '');

			// Post the reason text
			const addReasonResult = await basketApiService.postReason(basket.Id, basket.ProductId, reason);
			if (addReasonResult.err) return Err(addReasonResult.err);

			// Post files if there are any
			const fileTickets = fileManagerService.peakUploadedIdentities();
			if (fileTickets.length > 0) {
				const addFilesResult = await basketApiService.postFiles(
					basket.Id,
					basket.ProductId,
					fileTickets.map((identity) => ({
						FileReference: identity,
					})),
				);

				if (addFilesResult.err) return Err(addFilesResult.err);
			}

			// If the basket is valid, we can commit it.
			const commitResult = await basketApiService.commit(basket.Id);
			if (commitResult.err) return Err(commitResult.err);

			switch (commitResult.ok.data.Status) {
				case CommitResultStatus.ValidationErrors:
					if (commitResult.ok.data.ValidationResult === ValidationResultType.FormMissingResponse) {
						return Err(I18nAndL10n.translate('View.CreateNewMessage.ScreenConclusion.FormValidationError'));
					} else {
						return Err(I18nAndL10n.translate('Error.Patient.Unknown'));
					}
				case CommitResultStatus.PendingPayment:
					if (!commitResult.ok.data.PaymentBillId || !commitResult.ok.data.CommitJobId) {
						return Err(new NonRecoverableError('No payment bill id'));
					}

					return Ok({
						paymentBillId: commitResult.ok.data.PaymentBillId,
						commitJobId: commitResult.ok.data.CommitJobId,
					});
				case CommitResultStatus.CommitInProgress:
				case CommitResultStatus.ReservationCommitted:
					// Just continue
					break;
				default:
					return Err(I18nAndL10n.translate('Error.Patient.Unknown'));
			}

			if (!commitResult.ok.data.CommitJobId) {
				return Err(new NonRecoverableError('No commit job id'));
			}

			// Then we have to get the commit status to find out if the commit was
			// successful.
			return await waitForCommitStatus(commitResult.ok.data.CommitJobId);
		},
		[basketApiService, flowSettings, fileManagerService, waitForCommitStatus],
	);

	return {
		termsOfServiceUrl,
		submit,
		waitForCommitStatus,
	};
}
